import React from "react";
import type { ReactNode } from "react";

import { cn } from "./cn";
import { CheckIcon, InfoIcon, WarningIcon } from "./icons";

export type CalloutVariant = "info" | "warning" | "success";

const VARIANT_ICON = {
  info: InfoIcon,
  warning: WarningIcon,
  success: CheckIcon,
} as const;

const VARIANT_STYLE: Record<CalloutVariant, string> = {
  info: "border-l-primary/50 bg-primary/5",
  warning: "border-l-amber-500/70 bg-amber-500/10",
  success: "border-l-emerald-500/70 bg-emerald-500/10",
};

const VARIANT_ICON_STYLE: Record<CalloutVariant, string> = {
  info: "text-primary/70",
  warning: "text-amber-600 dark:text-amber-400",
  success: "text-emerald-600 dark:text-emerald-400",
};

/**
 * The one place info/warning copy gets visual weight: tinted background, left
 * rule, icon. A left rule rather than a full box, and muted body text rather
 * than tinted, so a warning reads as an aside to the step it sits in instead
 * of competing with the decision cards above it for attention.
 */
export function Callout({
  variant = "info",
  children,
  className,
}: {
  variant?: CalloutVariant;
  children: ReactNode;
  className?: string;
}) {
  const Icon = VARIANT_ICON[variant];

  return (
    <div
      className={cn(
        "flex gap-2.5 rounded-md border-l-4 p-3 text-sm text-muted-foreground",
        VARIANT_STYLE[variant],
        className
      )}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", VARIANT_ICON_STYLE[variant])} />
      <div className="flex-1">{children}</div>
    </div>
  );
}
